/**
 * DowngradeConfirmModal Component
 * 
 * Modal de confirmação ao fazer downgrade de plano
 * - Avisa sobre perda de features
 * - Informa que downgrade acontece na próxima renovação
 * - Permite cancelar a operação
 * 
 * Fase 5 - UI de Pagamentos
 */

import { AlertTriangle, X, XCircle } from 'lucide-react';

interface DowngradeConfirmModalProps {
  currentPlan: 'basic' | 'premium';
  targetPlan: 'free' | 'basic';
  onConfirm: () => void;
  onCancel: () => void;
}

export function DowngradeConfirmModal({ 
  currentPlan, 
  targetPlan, 
  onConfirm, 
  onCancel 
}: DowngradeConfirmModalProps) {
  
  // Features que serão perdidas
  const lostFeatures = {
    'premium-to-basic': [
      'Consultas SOS ilimitadas → 3/mês',
      '10 membros → 5 membros',
      'Análise com IA',
      'Suporte prioritário'
    ],
    'premium-to-free': [
      'Consultas SOS ilimitadas → 3 vitalícios',
      '10 membros → 3 membros',
      'Análise de prescrição ilimitado → 30 dias',
      'Análise com IA',
      'Suporte prioritário'
    ],
    'basic-to-free': [
      '3 consultas SOS/mês → 3 vitalícios',
      '5 membros → 3 membros',
      'Análise de prescrição ilimitado → 30 dias',
      'Suporte por email'
    ]
  };

  const downgradeKey = `${currentPlan}-to-${targetPlan}` as keyof typeof lostFeatures;
  const features = lostFeatures[downgradeKey] || [];

  const planNames = {
    free: 'Gratuito',
    basic: 'Básico',
    premium: 'Premium'
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-xl font-bold">Confirmar Downgrade</h3>
              <p className="text-gray-600 text-sm mt-1">
                Você está alterando de <strong>{planNames[currentPlan]}</strong> para{' '}
                <strong>{planNames[targetPlan]}</strong>
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Aviso sobre perda de features */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
          <p className="text-sm text-yellow-800 font-semibold mb-2">
            ⚠️ Você perderá acesso às seguintes features:
          </p>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-yellow-700">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Informação sobre quando acontece */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Quando acontece:</strong> O downgrade acontecerá na sua próxima renovação.
            Você continuará com acesso às features do plano <strong>{planNames[currentPlan]}</strong> até lá.
          </p>
        </div>

        {/* Botões */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onConfirm}
            className="w-full py-3 bg-yellow-600 text-white rounded-xl font-semibold hover:bg-yellow-700 transition-all"
          >
            Confirmar Downgrade
          </button>
          <button
            onClick={onCancel}
            className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
          >
            Cancelar
          </button>
        </div>

        {/* Nota final */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Essa ação é irreversível. Uma vez selecionada, o downgrade irá acontecer na próxima renovação
        </p>
      </div>
    </div>
  );
}
